import React, { useCallback, useEffect, useRef, useState } from 'react'

import type {
  ImageMetrics,
  OriginalDimensions,
} from '@ai-color-modules/simulation/hooks/useImageMetrics'

import { MidpointMarker } from './components/MidpointMarker'
import { DOUBLE_TAP_THRESHOLD_MS } from './constants'
import { VertexMarker } from './components/VertexMarker'
import { findNearestEdge, type Point } from './utils/edgeHitTest'

export interface AreaPoint {
  originalX: number
  originalY: number
}

export interface AreaData {
  id: string | number
  points: AreaPoint[]
  polygons?: AreaPoint[][]
}

/** 頂点挿入時に渡される情報 */
export interface InsertPointInfo {
  /** 挿入する辺のインデックス（この辺の後に挿入される） */
  edgeIndex: number
  /** 挿入する座標（オリジナル座標系） */
  originalX: number
  originalY: number
  /** ポリゴンのインデックス: -1 = points (メインポリゴン), 0以上 = polygons[n] */
  polygonIndex: number
}

interface AreaOverlayProps {
  areas: AreaData[]
  selectedAreaIndex: number | null
  originalDimensions: OriginalDimensions
  getMetrics: () => ImageMetrics
  onOverlayClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void
  onOverlayMove: (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => void
  onDragStart: (
    e: React.MouseEvent | React.TouchEvent,
    pointIndex: number,
    polygonIndex: number,
  ) => void
  onDragEnd: () => void
  /** 辺上に頂点を挿入するハンドラ */
  onInsertPoint?: (info: InsertPointInfo) => void
  /** 頂点を削除するハンドラ（ダブルクリック/ダブルタップ時） */
  onDeletePoint?: (pointIndex: number, polygonIndex: number) => void
  /** 選択中の頂点（強調表示に使う）。polygonIndex: -1 = points, 0以上 = polygons[n] */
  selectedVertex?: { pointIndex: number; polygonIndex: number } | null
  /** 頂点の右クリック（削除メニュー表示用）。クライアント座標付きで通知する */
  onVertexContextMenu?: (
    pointIndex: number,
    polygonIndex: number,
    clientX: number,
    clientY: number,
  ) => void
  /**
   * キャンバス移動モード中に true。オーバーレイのポインター操作を無効化し、
   * 下層の ZoomableImage にパン操作を通す
   */
  interactionDisabled?: boolean
  /**
   * ZoomableImage の現在のズーム倍率。頂点マーカーのサイズ・ヒット判定しきい値を
   * 1/zoomScale で補正し、ズームに依らずスクリーン上一定に保つ
   * (これが無いと拡大時にヒット領域も拡大され、カーソルから離れた場所が選択される)
   */
  zoomScale?: number
  isDragging?: boolean
  /** ドラッグ中の頂点インデックス */
  draggingPointIndex?: number | null
  /** ドラッグ中のポリゴンインデックス: -1 = points, 0以上 = polygons[n] */
  draggingPolygonIndex?: number
  selectedColor?: string
  unselectedColor?: string
  selectedFillColor?: string
  unselectedFillColor?: string
  overlayTestId?: string
  pointTestId?: string
}

/** ホバー中の辺情報 */
interface HoveredEdge {
  edgeIndex: number
  midpoint: Point
  /** ポリゴンのインデックス: -1 = points (メインポリゴン), 0以上 = polygons[n] */
  polygonIndex: number
}

/**
 * 汎用的なエリア選択用のSVGオーバーレイコンポーネント
 * サブエリア選択、除外エリア選択など、複数のユースケースに対応
 *
 * @example
 * // サブエリア選択
 * <AreaOverlay
 *   areas={subAreas}
 *   selectedAreaIndex={selectedSubAreaIndex}
 *   selectedColor="#3b82f6"
 *   unselectedColor="#94a3b8"
 *   {...otherProps}
 * />
 *
 * @example
 * // 除外エリア選択
 * <AreaOverlay
 *   areas={excludeAreas}
 *   selectedAreaIndex={selectedExcIdx}
 *   selectedColor="orange"
 *   unselectedColor="rgba(0,0,0,0.4)"
 *   overlayTestId="result-overlay"
 *   pointTestId="exclude-point"
 *   {...otherProps}
 * />
 */
export const AreaOverlay: React.FC<AreaOverlayProps> = ({
  areas,
  selectedAreaIndex,
  originalDimensions,
  getMetrics,
  onOverlayClick,
  onOverlayMove,
  onDragStart,
  onDragEnd,
  onInsertPoint,
  onDeletePoint,
  selectedVertex = null,
  onVertexContextMenu,
  interactionDisabled = false,
  zoomScale = 1,
  isDragging = false,
  draggingPointIndex,
  draggingPolygonIndex = -1,
  selectedColor = '#3b82f6',
  unselectedColor = '#94a3b8',
  selectedFillColor = 'rgba(59, 130, 246, 0.1)',
  unselectedFillColor = 'rgba(148, 163, 184, 0.1)',
  overlayTestId,
  pointTestId,
}) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [hoveredEdge, setHoveredEdge] = useState<HoveredEdge | null>(null)
  const lastTapRef = useRef<{ time: number; x: number; y: number } | null>(null)
  /** Apple Pencil (pen) のpointerdown位置を記録 */
  const penDownRef = useRef<{ x: number; y: number } | null>(null)
  /** Apple Pencil (pen) のシングルタップ遅延タイマー（ダブルタップとの区別用） */
  const pendingPenTapRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cursorStyle = interactionDisabled ? 'grab' : isDragging ? 'grabbing' : 'crosshair'

  // キャンバス移動モード (interactionDisabled) へドラッグ中に切り替わった場合、
  // オーバーレイが pointer-transparent になり mouseup を受け取れなくなるため、
  // ここでドラッグを強制終了する。放置すると isDragging が立ちっぱなしになり
  // ZoomableImage 全体が無効化されたまま (パン不能) + ホバーで頂点が動き続ける
  useEffect(() => {
    if (interactionDisabled && isDragging) {
      onDragEnd()
    }
  }, [interactionDisabled, isDragging, onDragEnd])

  // getMetrics()を一度だけ呼び出して、SVG配置とポイント計算で共有
  const { dispW, dispH, offX, offY } = getMetrics()

  // 選択中エリアの全ポリゴン表示座標配列を取得
  // { polygonIndex: number, points: Point[] }[] の形式で返す
  const getSelectedAreaAllPolygons = useCallback((): Array<{
    polygonIndex: number
    points: Point[]
  }> => {
    if (selectedAreaIndex === null || !areas[selectedAreaIndex]) {
      return []
    }
    const area = areas[selectedAreaIndex]
    const result: Array<{ polygonIndex: number; points: Point[] }> = []

    // メインポリゴン (points)
    if (area.points.length > 0) {
      result.push({
        polygonIndex: -1,
        points: area.points.map(p => ({
          x: (p.originalX / originalDimensions.width) * dispW,
          y: (p.originalY / originalDimensions.height) * dispH,
        })),
      })
    }

    // 追加ポリゴン (polygons)
    if (area.polygons) {
      area.polygons.forEach((polygon, idx) => {
        if (polygon.length > 0) {
          result.push({
            polygonIndex: idx,
            points: polygon.map(p => ({
              x: (p.originalX / originalDimensions.width) * dispW,
              y: (p.originalY / originalDimensions.height) * dispH,
            })),
          })
        }
      })
    }

    return result
  }, [areas, selectedAreaIndex, originalDimensions, dispW, dispH])

  // マウス移動時に辺のホバー検出（全ポリゴン対応）
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      // 元のハンドラを呼び出す
      onOverlayMove(e)

      // ドラッグ中はホバー検出しない
      if (isDragging) {
        setHoveredEdge(null)
        return
      }

      // onInsertPointがない場合はホバー検出しない
      if (!onInsertPoint) {
        return
      }

      const rect = overlayRef.current?.getBoundingClientRect()
      if (!rect) return

      const touch = 'touches' in e ? e.touches[0] : undefined
      const clientX = touch?.clientX ?? ('clientX' in e ? e.clientX : 0)
      const clientY = touch?.clientY ?? ('clientY' in e ? e.clientY : 0)

      // rectサイズとdispサイズの比率を計算（CSS transformのscaleを考慮）
      const scaleX = rect.width / dispW
      const scaleY = rect.height / dispH

      const mousePos: Point = {
        x: (clientX - rect.left) / scaleX,
        y: (clientY - rect.top) / scaleY,
      }

      const allPolygons = getSelectedAreaAllPolygons()
      if (allPolygons.length === 0) {
        setHoveredEdge(null)
        return
      }

      // 全ポリゴンの全頂点をフラットに収集
      const allVertices = allPolygons.flatMap(p => p.points)

      // 頂点の近くにいる場合は中間点マーカーを表示しない
      // (しきい値は display 座標系なのでズーム倍率で割り、スクリーン上の距離感を一定に保つ)
      const vertexThreshold = 8 / zoomScale
      const vertexThresholdSq = vertexThreshold * vertexThreshold
      const isNearVertex = allVertices.some(p => {
        const dx = mousePos.x - p.x
        const dy = mousePos.y - p.y
        return dx * dx + dy * dy < vertexThresholdSq
      })
      if (isNearVertex) {
        setHoveredEdge(null)
        return
      }

      // 全ポリゴンから最も近い辺を探す
      let bestEdge: HoveredEdge | null = null
      let bestDistance = Infinity

      for (const { polygonIndex, points } of allPolygons) {
        if (points.length < 2) continue

        const nearestEdge = findNearestEdge(mousePos, points, 20 / zoomScale)
        if (nearestEdge && nearestEdge.distance < bestDistance) {
          bestDistance = nearestEdge.distance
          bestEdge = {
            edgeIndex: nearestEdge.edgeIndex,
            midpoint: nearestEdge.closestPoint,
            polygonIndex,
          }
        }
      }

      setHoveredEdge(bestEdge)
    },
    [onOverlayMove, isDragging, onInsertPoint, dispW, dispH, getSelectedAreaAllPolygons, zoomScale],
  )

  // 中間点クリック時のハンドラ
  const handleMidpointClick = useCallback(() => {
    if (!hoveredEdge || !onInsertPoint) return

    // 表示座標をオリジナル座標に変換
    const originalX = (hoveredEdge.midpoint.x / dispW) * originalDimensions.width
    const originalY = (hoveredEdge.midpoint.y / dispH) * originalDimensions.height

    onInsertPoint({
      edgeIndex: hoveredEdge.edgeIndex,
      originalX,
      originalY,
      polygonIndex: hoveredEdge.polygonIndex,
    })

    setHoveredEdge(null)
  }, [hoveredEdge, onInsertPoint, dispW, dispH, originalDimensions])

  /**
   * ダブルタップによる中間点挿入の共通ロジック
   * touch/pen両方から呼び出される
   * @returns true: ダブルタップとして処理した, false: ダブルタップではなかった（1回目のタップを記録）
   */
  const tryDoubleTapInsert = useCallback(
    (clientX: number, clientY: number): boolean => {
      if (!onInsertPoint || isDragging) return false

      const now = Date.now()
      const lastTap = lastTapRef.current

      if (lastTap && now - lastTap.time < DOUBLE_TAP_THRESHOLD_MS) {
        const dx = Math.abs(clientX - lastTap.x)
        const dy = Math.abs(clientY - lastTap.y)

        // 20px以内の位置でダブルタップ
        if (dx < 20 && dy < 20) {
          const rect = overlayRef.current?.getBoundingClientRect()
          if (!rect) return false

          const scaleX = rect.width / dispW
          const scaleY = rect.height / dispH

          const tapPos: Point = {
            x: (clientX - rect.left) / scaleX,
            y: (clientY - rect.top) / scaleY,
          }

          const allPolygons = getSelectedAreaAllPolygons()
          if (allPolygons.length === 0) return false

          let bestEdge: { edgeIndex: number; closestPoint: Point; polygonIndex: number } | null =
            null
          let bestDistance = Infinity

          for (const { polygonIndex, points } of allPolygons) {
            if (points.length < 2) continue
            const nearestEdge = findNearestEdge(tapPos, points, 30 / zoomScale)
            if (nearestEdge && nearestEdge.distance < bestDistance) {
              bestDistance = nearestEdge.distance
              bestEdge = {
                edgeIndex: nearestEdge.edgeIndex,
                closestPoint: nearestEdge.closestPoint,
                polygonIndex,
              }
            }
          }

          if (bestEdge) {
            const originalX = (bestEdge.closestPoint.x / dispW) * originalDimensions.width
            const originalY = (bestEdge.closestPoint.y / dispH) * originalDimensions.height
            onInsertPoint({
              edgeIndex: bestEdge.edgeIndex,
              originalX,
              originalY,
              polygonIndex: bestEdge.polygonIndex,
            })
          }

          lastTapRef.current = null
          return true
        }
      }

      lastTapRef.current = { time: now, x: clientX, y: clientY }
      return false
    },
    [
      onInsertPoint,
      isDragging,
      dispW,
      dispH,
      originalDimensions,
      getSelectedAreaAllPolygons,
      zoomScale,
    ],
  )

  // タッチデバイス対応: ダブルタップで中間点挿入
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      const touch = e.changedTouches[0]
      if (!touch) return
      tryDoubleTapInsert(touch.clientX, touch.clientY)
    },
    [tryDoubleTapInsert],
  )

  // Apple Pencil (pen) 対応: pointerdown で位置を記録し、touchイベントの二重発火を防止
  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'pen') {
      e.preventDefault()
      penDownRef.current = { x: e.clientX, y: e.clientY }
    }
  }, [])

  // Apple Pencil (pen) 対応: pointermove でドラッグ移動を処理
  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'pen') {
        handleMouseMove(e as React.MouseEvent<HTMLDivElement>)
      }
    },
    [handleMouseMove],
  )

  // Apple Pencil (pen) 対応: pointerup でタップ（ポイント追加）とダブルタップ（中間点挿入）を処理
  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType !== 'pen') return

      const downPos = penDownRef.current
      penDownRef.current = null
      if (!downPos) return

      const dx = Math.abs(e.clientX - downPos.x)
      const dy = Math.abs(e.clientY - downPos.y)

      // 移動距離が小さい場合のみタップとして処理（パン操作との区別）
      if (dx < 5 && dy < 5) {
        // pendingタイマーがあればキャンセル（ダブルタップの2回目）
        if (pendingPenTapRef.current) {
          clearTimeout(pendingPenTapRef.current)
          pendingPenTapRef.current = null
        }

        const handled = tryDoubleTapInsert(e.clientX, e.clientY)
        if (!handled) {
          // シングルタップを遅延実行（ダブルタップとの区別のため）
          const { clientX, clientY, currentTarget } = e
          pendingPenTapRef.current = setTimeout(() => {
            pendingPenTapRef.current = null
            onOverlayClick({
              clientX,
              clientY,
              currentTarget,
            } as React.MouseEvent<HTMLDivElement, MouseEvent>)
          }, DOUBLE_TAP_THRESHOLD_MS)
        }
      }
    },
    [tryDoubleTapInsert, onOverlayClick],
  )

  // マウスがオーバーレイから離れた時
  const handleMouseLeave = useCallback(() => {
    setHoveredEdge(null)
  }, [])

  return (
    <div
      ref={overlayRef}
      className="absolute"
      data-testid={overlayTestId}
      onClick={onOverlayClick}
      onMouseLeave={handleMouseLeave}
      onMouseMove={handleMouseMove}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleMouseMove}
      style={{
        left: offX,
        top: offY,
        width: dispW,
        height: dispH,
        cursor: cursorStyle,
        // キャンバス移動モード中はオーバーレイを素通しにしてパン操作を許可する
        pointerEvents: interactionDisabled ? 'none' : 'auto',
        touchAction: 'none',
      }}
    >
      {/* SVGを親divと同じサイズに配置 */}
      <svg
        className="absolute inset-0"
        style={{
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        {areas.map((area, idx) => {
          const polygons = [area.points, ...(area.polygons ?? [])].filter(p => p.length > 0)
          if (polygons.length === 0) return null

          const isSelected = idx === selectedAreaIndex
          const strokeColor = isSelected ? selectedColor : unselectedColor
          const fillColor = isSelected ? selectedFillColor : unselectedFillColor

          return (
            <g key={area.id}>
              {polygons.map((polygonPoints, polygonIndex) => {
                const displayPoints = polygonPoints.map(p => ({
                  x: (p.originalX / originalDimensions.width) * dispW,
                  y: (p.originalY / originalDimensions.height) * dispH,
                }))

                return polygonPoints.length >= 3 ? (
                  <polygon
                    key={polygonIndex}
                    fill={fillColor}
                    points={displayPoints.map(p => `${p.x},${p.y}`).join(' ')}
                    stroke={strokeColor}
                    strokeWidth="2"
                  />
                ) : (
                  <polyline
                    key={polygonIndex}
                    fill="none"
                    points={displayPoints.map(p => `${p.x},${p.y}`).join(' ')}
                    stroke={strokeColor}
                    strokeWidth="2"
                  />
                )
              })}

              {/* 頂点マーカー（全ポリゴンに表示） */}
              {/* メインポリゴン (points) */}
              {area.points.map((p, pIdx) => {
                const displayPoint = {
                  x: (p.originalX / originalDimensions.width) * dispW,
                  y: (p.originalY / originalDimensions.height) * dispH,
                }
                return (
                  <VertexMarker
                    key={`main-${pIdx}`}
                    cx={displayPoint.x}
                    cy={displayPoint.y}
                    isDraggingThis={
                      isSelected && draggingPointIndex === pIdx && draggingPolygonIndex === -1
                    }
                    interactionDisabled={interactionDisabled}
                    isSelected={isSelected}
                    isVertexSelected={
                      isSelected &&
                      selectedVertex?.pointIndex === pIdx &&
                      selectedVertex?.polygonIndex === -1
                    }
                    onContextMenu={
                      onVertexContextMenu
                        ? (e, pi, poly) => onVertexContextMenu(pi, poly, e.clientX, e.clientY)
                        : undefined
                    }
                    markerScale={1 / zoomScale}
                    onDeletePoint={isSelected ? onDeletePoint : undefined}
                    onDragEnd={onDragEnd}
                    onDragStart={onDragStart}
                    pointIndex={pIdx}
                    polygonIndex={-1}
                    testId={pointTestId}
                  />
                )
              })}
              {/* 追加ポリゴン (polygons) */}
              {area.polygons?.map((polygon, polyIdx) =>
                polygon.map((p, pIdx) => {
                  const displayPoint = {
                    x: (p.originalX / originalDimensions.width) * dispW,
                    y: (p.originalY / originalDimensions.height) * dispH,
                  }
                  return (
                    <VertexMarker
                      key={`poly-${polyIdx}-${pIdx}`}
                      cx={displayPoint.x}
                      cy={displayPoint.y}
                      isDraggingThis={
                        isSelected &&
                        draggingPointIndex === pIdx &&
                        draggingPolygonIndex === polyIdx
                      }
                      interactionDisabled={interactionDisabled}
                      isSelected={isSelected}
                      isVertexSelected={
                        isSelected &&
                        selectedVertex?.pointIndex === pIdx &&
                        selectedVertex?.polygonIndex === polyIdx
                      }
                      onContextMenu={
                        onVertexContextMenu
                          ? (e, pi, poly) => onVertexContextMenu(pi, poly, e.clientX, e.clientY)
                          : undefined
                      }
                      markerScale={1 / zoomScale}
                      onDeletePoint={isSelected ? onDeletePoint : undefined}
                      onDragEnd={onDragEnd}
                      onDragStart={onDragStart}
                      pointIndex={pIdx}
                      polygonIndex={polyIdx}
                      testId={pointTestId}
                    />
                  )
                }),
              )}

              {/* 中間点マーカー（選択中エリアでホバー中の辺がある場合のみ表示） */}
              {isSelected && hoveredEdge && onInsertPoint && !interactionDisabled && (
                <MidpointMarker
                  color={selectedColor}
                  cx={hoveredEdge.midpoint.x}
                  cy={hoveredEdge.midpoint.y}
                  markerScale={1 / zoomScale}
                  onClick={handleMidpointClick}
                />
              )}
            </g>
          )
        })}
      </svg>
    </div>
  )
}
